import { FormattedMessage } from 'react-intl';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, Stack, Typography } from '@mui/material';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useDataProvider,
  FieldWrapperVertical,
  useUserAccess,
  SnackbarApiErrorMessage,
  useLocalization,
  useDialog,
  Select,
  useSnackbar,
  ReadOnly,
} from '@ui-framework/index';
import dayjs from 'dayjs';
import { toMediumDate } from '@utils/Dated';
import { nanoid } from 'nanoid';
import AcceptanceLocationFormSection from './AcceptanceLocationFormSection';
import PendingAcceptanceDialogBody from './PendingAcceptanceDialogBody';
import IconLibrary from '../IconLibrary';
import DialogCloseButton from './DialogCloseButton';
import {
  getDispatchEventLocation,
  toLocationOption,
} from '../FormFields/ServiceProviderRecommendedLocations';
import { CustomerServiceRequest } from '../FormFields';
import { TimeSlotTypeChipWithAfterBeforeHours } from '../TimeSlotTypeChip';

const SERVICE_PROVIDER_LOCATION_ID_PARAM = 'locationId';

function buildAcceptanceLocationYupFields(formatMessage) {
  return {
    acceptanceLocation: yup
      .mixed()
      .required(
        formatMessage({
          id: 'LOCATION_IS_REQUIRED',
          defaultMessage: 'Location is required',
        }),
      )
      .test(
        'acceptance-location-value',
        formatMessage({
          id: 'LOCATION_IS_REQUIRED',
          defaultMessage: 'Location is required',
        }),
        val =>
          val != null &&
          typeof val === 'object' &&
          !Array.isArray(val) &&
          val.value != null &&
          val.value !== '',
      ),
  };
}

function buildAcceptJobSchema({
  formatMessage,
  countOfCSR,
  singleCSRButInPast,
  requireAcceptanceLocation,
}) {
  const loc = requireAcceptanceLocation
    ? buildAcceptanceLocationYupFields(formatMessage)
    : {};

  if (countOfCSR > 1 || (countOfCSR === 1 && singleCSRButInPast)) {
    return yup.object().shape({
      customerServiceRequests: yup.string().required(
        formatMessage({
          id: 'APPOINTMENT_DATE_IS_REQUIRED',
          defaultMessage: 'Appointment Date is required',
        }),
      ),
      appointmentDate: yup.mixed().when('customerServiceRequests', {
        is: 'OTHER',
        then: s =>
          s.test(
            'request-date-required-not-in-past',
            formatMessage({
              id: 'DATE_IS_REQUIRED',
              defaultMessage: 'Date is required',
            }),
            (value, context) => {
              if (!value?.requestDate) {
                return context.createError({
                  path: `${context.path}.requestDate`,
                  message: formatMessage({
                    id: 'DATE_IS_REQUIRED',
                    defaultMessage: 'Date is required',
                  }),
                });
              }
              if (dayjs(value?.requestDate).isBefore(dayjs(), 'day')) {
                return context.createError({
                  path: `${context.path}.requestDate`,
                  message: formatMessage({
                    id: 'DATE_CANNOT_BE_IN_THE_PAST',
                    defaultMessage: 'Date cannot be in the past',
                  }),
                });
              }
              return true;
            },
          ),
        otherwise: s => s.notRequired(),
      }),
      ...loc,
    });
  }

  if (countOfCSR === 0) {
    return yup.object().shape({
      appointmentDate: yup.mixed().test(
        'request-date-required-not-in-past',
        formatMessage({
          id: 'DATE_IS_REQUIRED',
          defaultMessage: 'Date is required',
        }),
        (value, context) => {
          if (!value?.requestDate) {
            return context.createError({
              path: `${context.path}.requestDate`,
              message: formatMessage({
                id: 'DATE_IS_REQUIRED',
                defaultMessage: 'Date is required',
              }),
            });
          }
          if (dayjs(value?.requestDate).isBefore(dayjs(), 'day')) {
            return context.createError({
              path: `${context.path}.requestDate`,
              message: formatMessage({
                id: 'DATE_CANNOT_BE_IN_THE_PAST',
                defaultMessage: 'Date cannot be in the past',
              }),
            });
          }
          return true;
        },
      ),
      ...loc,
    });
  }

  if (requireAcceptanceLocation) {
    return yup.object().shape(loc);
  }

  return null;
}

function AcceptJobFormInner({
  schema,
  defaultValues,
  countOfCSR,
  singleCSRButInPast,
  customerServiceRequests,
  dateOptions,
  dispatchEventId,
  dispatchId,
  formatMessage,
  checkAndInjectDisabledForPastDates,
  onSubmit,
  onCancel,
}) {
  const methods = useForm({
    ...(schema && { resolver: yupResolver(schema) }),
    defaultValues,
  });

  const {
    handleSubmit,
    control: innerControl,
    formState: { errors: innerErrors },
  } = methods;

  const customerServiceRequestsValue = useWatch({
    control: innerControl,
    name: 'customerServiceRequests',
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box>
            <AcceptanceLocationFormSection
              dispatchEventId={dispatchEventId}
              dispatchId={dispatchId}
              errors={innerErrors}
              serviceJobId={defaultValues?.serviceJobId}
              name="acceptanceLocation"
            />
            {countOfCSR === 0 && (
              <Controller
                name="appointmentDate"
                control={innerControl}
                render={({ field }) => {
                  return (
                    <CustomerServiceRequest permission="WRITE" {...field} />
                  );
                }}
              />
            )}
            {countOfCSR === 1 && !singleCSRButInPast && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="left"
                flexWrap="nowrap">
                <Typography component="span">
                  <FormattedMessage
                    id="DO_YOU_WANT_TO_ACCEPT_THE_JOB_FOR"
                    defaultMessage="Do you want to accept the job for"
                  />
                </Typography>
                <ReadOnly>{customerServiceRequests[0]?.requestDate}</ReadOnly>
                <TimeSlotTypeChipWithAfterBeforeHours
                  code={customerServiceRequests[0]?.code}
                  afterHour={customerServiceRequests[0]?.afterHour}
                  beforeHour={customerServiceRequests[0]?.beforeHour}
                />
                <Typography component="span">?</Typography>
              </Stack>
            )}
            {((countOfCSR === 1 && singleCSRButInPast) || countOfCSR > 1) && (
              <Box>
                <Box>
                  <Controller
                    name="customerServiceRequests"
                    control={innerControl}
                    render={({ field }) => {
                      const { value, ...rest } = field;
                      return (
                        <FieldWrapperVertical
                          field={{
                            name: 'customerServiceRequests',
                            label: {
                              localeId: 'APPOINTMENT_DATE',
                              defaultMessage: 'Appointment Date',
                            },
                          }}
                          errors={innerErrors}>
                          <Select
                            {...rest}
                            options={checkAndInjectDisabledForPastDates(
                              dateOptions,
                            )}
                          />
                        </FieldWrapperVertical>
                      );
                    }}
                  />
                </Box>
                {customerServiceRequestsValue === 'OTHER' && (
                  <Box sx={{ mt: 2 }}>
                    <Controller
                      name="appointmentDate"
                      control={innerControl}
                      render={({ field }) => {
                        return (
                          <CustomerServiceRequest
                            permission="WRITE"
                            {...field}
                          />
                        );
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
          <Button
            type="Submit"
            variant="contained"
            color="primary"
            startIcon={<IconLibrary.Accept />}>
            {formatMessage({ id: 'ACCEPT', defaultMessage: 'Accept' })}
          </Button>
          <Button
            type="button"
            onClick={onCancel}
            variant="contained"
            color="secondary"
            startIcon={<IconLibrary.Cancel />}>
            {formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' })}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
}

function AcceptJobDialog({ open, onClose, jobInfo, dispatchEventId }) {
  const { formatMessage } = useLocalization();
  const userAccess = useUserAccess();
  const snackbar = useSnackbar();
  const serviceProviderId = userAccess?.company?.id;
  const { confirmationRef } = useDialog();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put, get } = useDataProvider(provider);
  const { id: jobId, customerServiceRequests = [], serviceJobNumber } = jobInfo;
  const dialogDispatchEventId = dispatchEventId || jobInfo?.dispatchEvent?.id;

  const dialogDispatchId = jobInfo?.dispatchEvent?.dispatchId;

  const countOfCSR = Array.isArray(customerServiceRequests)
    ? customerServiceRequests?.length
    : 0;

  let singleCSRButInPast = false;

  const dateOptions =
    customerServiceRequests?.map(date => ({
      label: (
        <Stack direction="row" spacing={1}>
          <Typography>{toMediumDate(date?.requestDate)}</Typography>
          <TimeSlotTypeChipWithAfterBeforeHours
            afterHour={date?.afterHour}
            beforeHour={date?.beforeHour}
            code={date?.slotTypeCode}
            smallAfterAndBefore
          />
        </Stack>
      ),
      value: JSON.stringify(date),
    })) || [];

  if (countOfCSR === 1) {
    const now = dayjs();
    if (
      dayjs(JSON.parse(dateOptions[0].value)?.requestDate).isBefore(now, 'day')
    ) {
      singleCSRButInPast = true;
    }
  }

  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({
    serviceJobId: jobId,
    serviceProviderId,
    acceptanceLocation: null,
  });

  const schema = useMemo(
    () =>
      buildAcceptJobSchema({
        formatMessage,
        countOfCSR,
        singleCSRButInPast,
        requireAcceptanceLocation: true,
      }),
    [formatMessage, countOfCSR, singleCSRButInPast],
  );

  const getJobDispatchId = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/pending-acceptance/${jobId}`,
    );
    if (response.ok) {
      return response?.data?.dispatchEvent?.id;
    }
    return null;
  };

  const onFormSubmit = async data => {
    setLoading(true);
    const dispatchEventValue =
      dialogDispatchEventId || (await getJobDispatchId());
    if (!dispatchEventValue) {
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'ERROR_COULD_NOT_ACCEPT_THE_JOB',
          defaultMessage: 'Error: Could not accept the job',
        }),
      });
      setLoading(false);
      return;
    }

    const locationId = data?.acceptanceLocation?.value ?? null;
    if (!locationId) {
      setLoading(false);
      return;
    }

    let body = {};

    if (countOfCSR === 0) {
      body = {
        serviceDate: data.appointmentDate?.requestDate,
        slotTypeCode: data.appointmentDate?.slotTypeCode,
      };
    } else if (countOfCSR === 1 && !singleCSRButInPast) {
      body = {
        serviceDate: customerServiceRequests[0].requestDate,
        slotTypeCode: customerServiceRequests[0].slotTypeCode,
      };
    } else if (countOfCSR > 1 || (countOfCSR === 1 && singleCSRButInPast)) {
      if (data?.customerServiceRequests === 'OTHER') {
        body = {
          serviceDate: data.appointmentDate?.requestDate,
          slotTypeCode: data.appointmentDate?.slotTypeCode,
        };
      } else {
        const csrDate = JSON.parse(data?.customerServiceRequests);
        body = {
          serviceDate: csrDate.requestDate,
          slotTypeCode: csrDate.slotTypeCode,
        };
      }
    }

    const finalBody = {
      dispatchEventId: dispatchEventValue,
      ...body,
      jobRecordVersion: jobInfo.recordVersion,
    };

    const query = new URLSearchParams();
    query.set(SERVICE_PROVIDER_LOCATION_ID_PARAM, locationId);
    const response = await put(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/pending-acceptance/${jobId}/accept?${query.toString()}`,
      finalBody,
    );

    if (response.ok) {
      confirmationRef.current.init({
        message: 'Job was accepted successfully',
        Icon: <IconLibrary.ServiceJob />,
        positiveButtonText: 'Close',
        positiveButtonIcon: <IconLibrary.Close />,
        width: 360,
        title: 'Job Accepted',
        onChange: () => {
          confirmationRef.current.setOpen(false);
          onClose(nanoid());
        },
      });
      confirmationRef.current.setOpen(true);
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    setLoading(false);
  };

  const checkAndInjectDisabledForPastDates = useCallback(options => {
    const now = dayjs();
    let allDisabled = true;
    const finalOptions = options.map(opt => {
      const isDisabled = dayjs(JSON.parse(opt.value)?.requestDate).isBefore(
        now,
        'day',
      );
      if (!isDisabled) {
        allDisabled = false;
      }
      return {
        ...opt,
        disabled: isDisabled,
      };
    });
    if (allDisabled) {
      finalOptions.push({
        label: 'Other',
        value: 'OTHER',
      });
    }
    return finalOptions;
  }, []);

  const onCancel = useCallback(() => onClose(null), [onClose]);

  const initialize = async () => {
    setLoading(true);
    const dispatchEventLocation = await getDispatchEventLocation({
      serviceJobId: jobId,
      dispatchId: dialogDispatchId,
      dispatchEventId: dialogDispatchEventId,
      serviceProviderId,
      apiUrl: getApiUrl(),
      get,
      snackbar,
    });
    setInitialValues(prev => ({
      ...prev,
      acceptanceLocation: toLocationOption(dispatchEventLocation),
    }));
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose(null);
      }}
      slotProps={{
        paper: {
          sx: {
            maxWidth: '850px',
            width: '100%',
          },
        },
      }}
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.AcceptServiceJob />
        {formatMessage({
          id: 'ACCEPT_JOB',
          defaultMessage: 'Accept Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {serviceJobNumber}
        </Typography>
        <DialogCloseButton
          disabled={loading}
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>
      <PendingAcceptanceDialogBody loading={loading}>
        <AcceptJobFormInner
          key={jobId}
          schema={schema}
          defaultValues={initialValues}
          countOfCSR={countOfCSR}
          singleCSRButInPast={singleCSRButInPast}
          customerServiceRequests={customerServiceRequests}
          dateOptions={dateOptions}
          dispatchEventId={dialogDispatchEventId}
          dispatchId={dialogDispatchId}
          formatMessage={formatMessage}
          checkAndInjectDisabledForPastDates={
            checkAndInjectDisabledForPastDates
          }
          onSubmit={onFormSubmit}
          onCancel={onCancel}
        />
      </PendingAcceptanceDialogBody>
    </Dialog>
  );
}

export default AcceptJobDialog;
